import { useCallback, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/i18n";
import AppShell from "@/components/AppShell";
import TemplatesBar from "@/components/TemplatesBar";
import { useCanvasScale, eventToPreviewCoords } from "@/lib/canvasScale";
import { supportsFolderWrite, writeZipToFolder, downloadZip } from "@/lib/folderWriter";
import FolderWriteErrorDialog from "@/components/FolderWriteErrorDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Upload, FileText, Trash2, MousePointerClick, Download,
  ChevronLeft, ChevronRight, Loader2, PlusCircle, Table2,
} from "lucide-react";

export default function CsvBatchPage() {
  const t = useT();
  const [previewPages, setPreviewPages] = useState([]);
  const [scale, setScale] = useState(1);
  const [templateFile, setTemplateFile] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [fields, setFields] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState({ headers: [], rows: [] });
  const [processing, setProcessing] = useState(false);
  const [folderError, setFolderError] = useState(null);
  const lastBlobRef = useRef({ blob: null, filename: "" });

  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const page = previewPages[currentPage];
  const { outerRef, displayScale } = useCanvasScale(page?.preview_width);

  const handleTemplateUpload = async (file) => {
    if (!file) return;
    setLoadingPreview(true);
    setPreviewPages([]); setFields([]); setSelectedId(null); setCurrentPage(0);
    setTemplateFile(file);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pdf/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreviewPages(data.pages); setScale(data.scale);
      toast.success(t("csvBatch.toasts.templateLoaded", { n: data.pages.length }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoadingPreview(false); }
  };

  const handleCsvUpload = async (file) => {
    if (!file) return;
    setCsvFile(file);
    const text = await file.text();
    // Simple client-side preview (server does the real parse)
    const firstLine = text.split(/\r?\n/).find((l) => l.trim());
    const delim = [",", ";", "\t", "|"].find((d) => (firstLine || "").includes(d)) || ",";
    const lines = text.split(/\r?\n/).filter((l) => l.trim()).slice(0, 6);
    const headers = (lines[0] || "").split(delim).map((h) => h.trim().replace(/^"|"$/g, ""));
    const rows = lines.slice(1).map((l) => l.split(delim).map((v) => v.trim().replace(/^"|"$/g, "")));
    setCsvPreview({ headers, rows });
    toast.success(t("csvBatch.toasts.csvLoaded", { n: headers.length }));
  };

  const onCanvasClick = (e) => {
    if (!page) return;
    if (e.target.closest("[data-field-marker]")) return;
    const { x, y } = eventToPreviewCoords(e, canvasRef.current);
    const id = crypto.randomUUID();
    // Pick the next unused CSV column so each click auto-maps to a different column.
    // If all columns already used, cycle back by index.
    let defaultText = t("csvBatch.defaultText");
    if (csvPreview.headers.length > 0) {
      const usedCols = new Set();
      fields.forEach((f) => {
        const matches = (f.text || "").match(/\{([^}]+)\}/g) || [];
        matches.forEach((m) => usedCols.add(m.slice(1, -1)));
      });
      const nextCol =
        csvPreview.headers.find((h) => !usedCols.has(h)) ||
        csvPreview.headers[fields.length % csvPreview.headers.length];
      defaultText = `{${nextCol}}`;
    }
    setFields((prev) => [...prev, { id, page: currentPage, x, y, text: defaultText, font_size: 24 }]);
    setSelectedId(id);
  };

  const updateField = (id, patch) => setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const deleteField = (id) => { setFields((prev) => prev.filter((f) => f.id !== id)); if (selectedId === id) setSelectedId(null); };

  const onMarkerMouseDown = (e, f) => {
    e.stopPropagation(); setSelectedId(f.id);
    const { x, y } = eventToPreviewCoords(e, canvasRef.current);
    dragRef.current = { id: f.id, offsetX: x - f.x, offsetY: y - f.y };
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
  };
  const onDragMove = useCallback((e) => {
    const drag = dragRef.current; if (!drag || !canvasRef.current) return;
    const intrinsicW = canvasRef.current.offsetWidth || 1;
    const intrinsicH = canvasRef.current.offsetHeight || 1;
    const { x: mx, y: my } = eventToPreviewCoords(e, canvasRef.current);
    const x = Math.max(0, Math.min(intrinsicW, mx - drag.offsetX));
    const y = Math.max(0, Math.min(intrinsicH, my - drag.offsetY));
    setFields((prev) => prev.map((f) => (f.id === drag.id ? { ...f, x, y } : f)));
  }, []);
  const onDragEnd = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragEnd);
  }, [onDragMove]);

  const currentPageFields = useMemo(() => fields.filter((f) => f.page === currentPage), [fields, currentPage]);
  const selectedField = fields.find((f) => f.id === selectedId);

  const runMerge = async () => {
    if (!templateFile) return toast.error(t("csvBatch.toasts.needTemplate"));
    if (!csvFile) return toast.error(t("csvBatch.toasts.needCsv"));
    if (!fields.length) return toast.error(t("csvBatch.toasts.needField"));
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("template", templateFile);
      fd.append("csv_file", csvFile);
      fd.append("scale", String(scale));
      const cleaned = fields.map((f) => ({ page: f.page, x: f.x, y: f.y, text: f.text, font_size: f.font_size }));
      fd.append("fields", JSON.stringify(cleaned));
      const res = await api.post("/pdf/process-csv", fd, { responseType: "blob", headers: { "Content-Type": "multipart/form-data" } });
      const blob = new Blob([res.data], { type: "application/zip" });
      const baseName = templateFile?.name || "template";
      const zipName = `${baseName.replace(/\.[a-zA-Z0-9]+$/, "")}-output.zip`;
      lastBlobRef.current = { blob, filename: zipName };
      if (supportsFolderWrite()) {
        try {
          const result = await writeZipToFolder(blob, baseName);
          if (result.cancelled) {
            toast.info(t("common.saveCanceled"));
          } else {
            toast.success(t("csvBatch.toasts.savedInFolder", { n: result.count, folder: result.folderName }));
          }
        } catch (folderErr) {
          // eslint-disable-next-line no-console
          console.error("[folder-write]", folderErr, folderErr.diagnostics);
          setFolderError(folderErr);
        }
      } else {
        downloadZip(blob, zipName);
        toast.success(t("common.archiveDownloaded"));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setProcessing(false); }
  };

  const insertPlaceholder = (col) => {
    if (!selectedField) return toast.error(t("csvBatch.toasts.selectFieldFirst"));
    // Replace the entire field text with the selected column placeholder,
    // so each field maps to exactly one column.
    updateField(selectedField.id, { text: `{${col}}` });
  };

  // Which columns are already mapped to a field (for visual hint on chips)
  const usedColumns = new Set();
  fields.forEach((f) => {
    const matches = (f.text || "").match(/\{([^}]+)\}/g) || [];
    matches.forEach((m) => usedColumns.add(m.slice(1, -1)));
  });

  const subfolderName = templateFile ? `${templateFile.name.replace(/\.[a-zA-Z0-9]+$/, "")}-output` : "";

  return (
    <AppShell subtitle={templateFile?.name || t("csvBatch.subtitleEmpty")}>
      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr_340px]">
        {/* LEFT */}
        <aside className="border-r border-white/[0.08] p-6 space-y-6 overflow-y-auto" data-testid="csv-left-panel">
          <div>
            <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40">{t("csvBatch.step1")}</div>
            <h3 className="font-geist font-semibold text-2xl mt-2 mb-4">{t("csvBatch.step1Title")}</h3>
            <label className="block border border-dashed border-white/15 hover:border-[#4D8AFF] hover:bg-[#4D8AFF]/[0.06] transition-colors p-5 cursor-pointer rounded-sm text-center">
              <input type="file" accept="application/pdf" className="hidden" data-testid="csv-template-input" onChange={(e) => handleTemplateUpload(e.target.files?.[0])} />
              {loadingPreview ? (
                <><Loader2 className="h-5 w-5 animate-spin text-[#4D8AFF] mx-auto mb-2" /><div className="font-geist-mono text-xs">{t("common.processing")}</div></>
              ) : (
                <><Upload className="h-6 w-6 text-[#4D8AFF] mx-auto mb-2" /><div className="font-geist-mono text-xs">{t("csvBatch.dropCta")}</div></>
              )}
            </label>
          </div>

          <div>
            <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40">{t("csvBatch.step2")}</div>
            <h3 className="font-geist font-semibold text-2xl mt-2 mb-4">{t("csvBatch.step2Title")}</h3>
            <label className="block border border-dashed border-white/15 hover:border-[#4D8AFF] hover:bg-[#4D8AFF]/[0.06] transition-colors p-5 cursor-pointer rounded-sm text-center">
              <input type="file" accept=".csv" className="hidden" data-testid="csv-file-input" onChange={(e) => handleCsvUpload(e.target.files?.[0])} />
              <Table2 className="h-6 w-6 text-[#4D8AFF] mx-auto mb-2" />
              <div className="font-geist-mono text-xs">{csvFile ? csvFile.name : t("csvBatch.selectCsv")}</div>
              <div className="font-geist-mono text-[10px] text-white/40 mt-1">{t("csvBatch.oneRowOnePdf")}</div>
            </label>

            {csvPreview.headers.length > 0 && (
              <div className="mt-4" data-testid="csv-columns-list">
                <div className="font-geist-mono text-[10px] uppercase text-white/40 mb-2">
                  {t("csvBatch.columnsLabel")}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {csvPreview.headers.map((h) => {
                    const isUsed = usedColumns.has(h);
                    const isCurrent = selectedField && (selectedField.text || "").includes(`{${h}}`);
                    return (
                      <button
                        key={h}
                        data-testid={`csv-col-${h}`}
                        onClick={() => insertPlaceholder(h)}
                        className={`font-geist-mono text-[10px] px-2 py-1 border rounded-sm transition-colors ${
                          isCurrent
                            ? "bg-[#4D8AFF] text-[#050B1F] border-[#4D8AFF]"
                            : isUsed
                            ? "border-[#4D8AFF]/25 text-white/40 bg-[#4D8AFF]/[0.04] hover:bg-[#4D8AFF]/10 hover:text-[#4D8AFF]"
                            : "border-[#4D8AFF]/40 text-[#4D8AFF] hover:bg-[#4D8AFF] hover:text-[#050B1F]"
                        }`}
                        title={isUsed ? t("csvBatch.columnUsedTip") : t("csvBatch.columnFreeTip")}
                      >
                        {`{${h}}`}
                        {isUsed && !isCurrent && <span className="ml-1 opacity-60">•</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="font-geist-mono text-[10px] text-white/40 mt-2">
                  {t("csvBatch.columnsSummary", { used: usedColumns.size, total: csvPreview.headers.length, rows: csvPreview.rows.length })}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40">{t("csvBatch.step3")}</div>
            <Button
              data-testid="csv-process-btn"
              disabled={processing || !templateFile || !csvFile || !fields.length}
              onClick={runMerge}
              className="w-full mt-4 h-11 rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-medium"
            >
              {processing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.processing")}</> : supportsFolderWrite() ? <><Download className="h-4 w-4 mr-2" /> {t("common.saveFolder")}</> : <><Download className="h-4 w-4 mr-2" /> {t("common.downloadZip")}</>}
            </Button>
            <p className="font-geist-mono text-[10px] text-white/40 mt-2">
              {t("csvBatch.processHint", { ph: "{colonna}" })}
              {supportsFolderWrite() && templateFile && (
                <> {t("csvBatch.subfolder", { name: subfolderName })}</>
              )}
            </p>
          </div>

          <TemplatesBar
            fields={fields}
            scale={scale}
            pageCount={previewPages.length || 1}
            canSave={fields.length > 0 && previewPages.length > 0}
            onLoad={(tpl) => { setFields(tpl.fields.map((f) => ({ ...f, id: crypto.randomUUID() }))); setScale(tpl.scale); }}
          />
        </aside>

        {/* CENTER canvas */}
        <main ref={outerRef} className="p-8 dot-grid overflow-auto min-h-[600px]">
          {!page ? (
            <div className="h-full min-h-[500px] flex flex-col items-center justify-center text-center">
              <MousePointerClick className="h-12 w-12 text-[#4D8AFF] mb-6" strokeWidth={1.2} />
              <div className="font-geist font-semibold text-3xl max-w-md">{t("csvBatch.emptyCanvasTitle")}</div>
              <div className="font-geist-mono text-xs text-white/60 mt-4 max-w-md">
                {t("csvBatch.emptyCanvasBody", { ph: "{colonna}" })}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              {previewPages.length > 1 && (
                <div className="flex items-center gap-4 mb-4 font-geist-mono text-xs">
                  <Button variant="ghost" size="sm" disabled={currentPage === 0} onClick={() => setCurrentPage((p) => Math.max(0, p - 1))} className="rounded-sm">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span>{t("csvBatch.pageIndicator", { current: currentPage + 1, total: previewPages.length })}</span>
                  <Button variant="ghost" size="sm" disabled={currentPage >= previewPages.length - 1} onClick={() => setCurrentPage((p) => Math.min(previewPages.length - 1, p + 1))} className="rounded-sm">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <div
                style={{
                  width: page.preview_width * displayScale,
                  height: page.preview_height * displayScale,
                  overflow: "hidden",
                }}
              >
                <div
                  ref={canvasRef}
                  data-testid="csv-pdf-canvas"
                  onClick={onCanvasClick}
                  className="relative bg-white border border-white/25 shadow-[0_0_40px_-10px_rgba(77,138,255,0.35)] cursor-crosshair"
                  style={{
                    width: page.preview_width,
                    height: page.preview_height,
                    transform: `scale(${displayScale})`,
                    transformOrigin: "top left",
                  }}
                >
                  <img
                    key={page.image.slice(0, 64)}
                    src={page.image}
                    alt="template"
                    draggable={false}
                    className="absolute inset-0 w-full h-full pointer-events-none select-none"
                  />
                  {currentPageFields.map((f) => (
                    <div
                      key={f.id}
                      data-field-marker
                      onMouseDown={(e) => onMarkerMouseDown(e, f)}
                      onClick={(e) => { e.stopPropagation(); setSelectedId(f.id); }}
                      className={`field-marker ${selectedId === f.id ? "selected" : ""}`}
                      style={{ left: f.x, top: f.y, fontSize: f.font_size, lineHeight: 1.1 }}
                    >
                      {f.text || "…"}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </main>

        {/* RIGHT */}
        <aside className="border-l border-white/[0.08] p-6 overflow-y-auto bg-[#050B1F]/40">
          <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40">{t("csvBatch.rightTitle")}</div>
          <h3 className="font-geist font-semibold text-2xl mt-2 mb-4">{t("csvBatch.rightSub")}</h3>
          {fields.length === 0 ? (
            <div className="border border-dashed border-white/[0.08] rounded-sm p-6 text-center font-geist-mono text-xs text-white/40">
              <PlusCircle className="h-5 w-5 mx-auto mb-2 text-[#4D8AFF]" />
              {t("csvBatch.noFieldsHint")}
            </div>
          ) : (
            <div className="space-y-2">
              {fields.map((f, idx) => (
                <button
                  key={f.id}
                  onClick={() => { setCurrentPage(f.page); setSelectedId(f.id); }}
                  className={`w-full text-left px-3 py-2 border rounded-sm ${selectedId === f.id ? "border-[#4D8AFF] bg-[#4D8AFF]/10" : "border-white/[0.08] hover:border-white/40"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-geist-mono text-[10px] text-white/40">#{String(idx + 1).padStart(2, "0")} · pag {f.page + 1}</span>
                    <span className="font-geist-mono text-[10px] text-white/60">x{Math.round(f.x)} y{Math.round(f.y)}</span>
                  </div>
                  <div className="text-sm truncate mt-1">{f.text || t("csvBatch.emptyField")}</div>
                </button>
              ))}
            </div>
          )}
          {selectedField && (
            <div className="mt-6 pt-6 border-t border-white/[0.08] space-y-4">
              <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40">{t("csvBatch.editField")}</div>
              <div>
                <Label className="text-xs font-geist-mono uppercase tracking-wider text-white/60">{t("csvBatch.textWithCol", { colph: "{colonna}" })}</Label>
                <Input data-testid="csv-field-text" value={selectedField.text} onChange={(e) => updateField(selectedField.id, { text: e.target.value })} className="mt-2 rounded-sm h-10 font-geist-mono text-xs" />
              </div>
              <div>
                <Label className="text-xs font-geist-mono uppercase tracking-wider text-white/60">{t("csvBatch.fontSizeLabel")}</Label>
                <Input type="number" value={selectedField.font_size} min={8} max={200} onChange={(e) => updateField(selectedField.id, { font_size: Number(e.target.value) || 12 })} className="mt-2 rounded-sm h-10 font-geist-mono" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs font-geist-mono uppercase text-white/60">X</Label><Input type="number" value={Math.round(selectedField.x)} onChange={(e) => updateField(selectedField.id, { x: Number(e.target.value) || 0 })} className="mt-2 rounded-sm h-10 font-geist-mono" /></div>
                <div><Label className="text-xs font-geist-mono uppercase text-white/60">Y</Label><Input type="number" value={Math.round(selectedField.y)} onChange={(e) => updateField(selectedField.id, { y: Number(e.target.value) || 0 })} className="mt-2 rounded-sm h-10 font-geist-mono" /></div>
              </div>
              <Button variant="ghost" onClick={() => deleteField(selectedField.id)} className="w-full rounded-sm text-[#E63946] hover:bg-[#E63946]/10 hover:text-[#E63946]">
                <Trash2 className="h-4 w-4 mr-2" /> {t("csvBatch.deleteField")}
              </Button>
            </div>
          )}
        </aside>
      </div>
      <FolderWriteErrorDialog
        error={folderError}
        onClose={() => setFolderError(null)}
        onDownloadZip={() => {
          if (lastBlobRef.current.blob) {
            downloadZip(lastBlobRef.current.blob, lastBlobRef.current.filename);
            toast.success(t("common.archiveDownloaded"));
          }
        }}
      />
    </AppShell>
  );
}
